import { useCallback, useSyncExternalStore } from 'react';

/**
 * Minimal external-store primitives (useSyncExternalStore) for isolated leaf
 * subscriptions that must NOT become Parent-State (D-06). Verblieben ist der
 * Values-Version-Zaehler (FormularD-Anzeige-Summen, D-13), der Blattkomponenten
 * bei jeder Formularaenderung neu rendern laesst, ohne dass der Wert selbst je in
 * React-State landet.
 *
 * Der Validity-Store (Submit-Gate) ist mit Plan 20-04 (K11/D-08) ersatzlos
 * entfallen -- die Vollstaendigkeitspruefung liegt jetzt im Backend.
 *
 * Mit Plan 20-07 kommt der Warnungs-Store (D-09/D-10) dazu: die zweite,
 * nicht-blockierende Meldungsstufe wird bewusst NICHT in `form.errors` gemischt.
 * Mantine kennt je Feld nur eine Fehlerliste, und die Feldwrapper sind fest auf Rot
 * verdrahtet -- eine Mischung wuerde Fehler und Hinweise ununterscheidbar machen und
 * die rote Stufe entwerten.
 */

type Listener = () => void;

interface Store<T> {
  set(next: T): void;
  get(): T;
  subscribe(listener: Listener): () => void;
}

function createStore<T>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener>();
  return {
    set(next: T) {
      state = next;
      listeners.forEach((listener) => listener());
    },
    get: () => state,
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export interface ValuesVersionStore extends Store<number> {
  bump(): void;
}

export function createValuesVersionStore(): ValuesVersionStore {
  const store = createStore<number>(0);
  return {
    ...store,
    bump() {
      store.set(store.get() + 1);
    },
  };
}

export function useValuesVersion(store: Store<number>): number {
  return useSyncExternalStore(store.subscribe, store.get);
}

/**
 * Feldpfad -> Warnungstext, exakt die Form, die `mapViolations` liefert. Konsumenten
 * schlagen einen Hinweis damit genauso nach wie einen Fehler (`warnings[field]`).
 */
export type FieldWarnings = Record<string, string>;

export type WarningsStore = Store<FieldWarnings>;

/**
 * Haelt die Warnungen der letzten Blur-Validierung (D-10: nur beim Verlassen eines
 * Feldes, nie beim Tippen). Als externer Store statt als React-State, damit ein
 * eingehendes Ergebnis nur die abonnierenden Blattzellen neu rendert und nicht das
 * gesamte Formular -- dieselbe Begruendung wie beim Values-Version-Zaehler.
 */
export function createWarningsStore(): WarningsStore {
  return createStore<FieldWarnings>({});
}

export function useWarnings(store: WarningsStore): FieldWarnings {
  return useSyncExternalStore(store.subscribe, store.get);
}

/**
 * Feldpfad -> bereits formatierter Vorwert-Text (D-14: deutsches Zahlenformat, „–" fuer
 * leer). Die Formatierung passiert beim Befuellen des Stores, nicht beim Rendern -- damit
 * der Schnappschuss, den `usePreviousValue` (components/form/previousValues.ts) liefert,
 * ein primitiver String bleibt und `useSyncExternalStore` ihn mit `Object.is` vergleichen
 * kann.
 */
export type PreviousValues = Record<string, string>;

export type PreviousValuesStore = Store<PreviousValues>;

/**
 * Haelt die Vorjahreswerte (D-17) fuer die aktuell geoeffnete Meldung. `set` ersetzt wie
 * beim Warnungs-Store den gesamten Stand: ein Feld, das im Vorjahr nicht mehr existiert,
 * verschwindet dadurch, statt als Leiche stehen zu bleiben.
 *
 * Kein Hook fuer den gesamten Objekt-Stand -- die Feldkomponenten lesen ihren Vorwert
 * einzeln ueber `usePreviousValue(field)` aus `components/form/previousValues.ts`, nie den
 * ganzen Store. Ein Hook auf den vollen Stand waere genau die Rendering-Falle, die D-17 fuer
 * Formular D (280 Felder) ausdruecklich vermeidet.
 */
export function createPreviousValuesStore(): PreviousValuesStore {
  return createStore<PreviousValues>({});
}

/**
 * Feldpfad -> Server-Rechenergebnis (`POST /api/reports/calculate`), Formular D (D-05). Generisch
 * getippt (`Record<string, string | null>`) statt auf den formularspezifischen Ergebnistyp
 * festgelegt: diese Datei ist die formularunabhaengige Ebene und darf nicht auf eine konkrete
 * Formular-Ebene zurueckzeigen -- dieselbe Begruendung wie beim strukturellen Kontexttyp in
 * `components/form/previousValues.ts`. `null` ist als Wert ausdruecklich erlaubt (im Unterschied
 * zu `PreviousValues`): ein Server-Feld kann rechnerisch leer sein, ohne im Store zu fehlen.
 */
export type CalcValues = Record<string, string | null>;

export type CalcStore = Store<CalcValues>;

/**
 * Haelt die zuletzt vom Server gelieferten Summen fuer die aktuell geoeffnete Formular-D-Meldung.
 * `set` ersetzt wie bei den anderen Stores den gesamten Stand.
 */
export function createCalcStore(): CalcStore {
  return createStore<CalcValues>({});
}

/**
 * Liest den Server-Rechenwert genau EINES Schluessels.
 *
 * Der Schnappschuss ist der einzelne WERT, nicht das ganze Store-Objekt -- exakt das Muster von
 * `usePreviousValue` (`components/form/previousValues.ts`), NICHT von `useValuesVersion` oben:
 * bei 41 Anzeigestellen ist ein primitiver Snapshot der Unterschied zwischen „eine Zelle rendert"
 * und „alle 41 rendern". Ein Objekt-Snapshot loest zusaetzlich Reacts Warnung „The result of
 * getSnapshot should be cached" aus.
 */
export function useCalcValue(store: CalcStore, key: string): string | null | undefined {
  const getSnapshot = useCallback(() => store.get()[key], [store, key]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
}
